<template>
  <div class="container">
    <Toolbar class="mt-2 mb-2">
      <template #start>
        <h2>
          <span v-if="isUpdate">
            {{ $t('sitepage.update') }}
          </span>
          <span v-else>{{ $t('sitepage.create') }}</span>
        </h2>
      </template>
    </Toolbar>
    <form @submit.prevent="submit">
      <div class="p-input mb-3">
        <label
          for="code"
          class="required-input"
        >{{ $t('entity.site.code') }}</label>
        <InputText
          id="code"
          v-model="v$.site.code.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.site.code.$error }"
          type="text"
        />
        <div
          v-for="error of v$.site.code.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="name"
          class="required-input"
        >{{ $t('entity.site.name') }}</label>
        <InputText
          id="name"
          v-model="v$.site.name.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.site.name.$error }"
          type="text"
          maxlength="255"
        />
        <div
          v-for="error of v$.site.name.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="frenchDescription">{{ $t('entity.site.french_description') }}</label>
        <Textarea
          id="frenchDescription"
          v-model="v$.site.frenchDescription.$model"
          class="col-12 mt-2"
          type="text"
          :auto-resize="true"
          :class="{'invalid-input': v$.site.frenchDescription.$error }"
          maxlength="255"
        />
        <div
          v-for="error of v$.site.frenchDescription.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="englishDescription">{{ $t('entity.site.english_description') }}</label>
        <Textarea
          id="englishDescription"
          v-model="v$.site.englishDescription.$model"
          class="col-12 mt-2"
          type="text"
          :auto-resize="true"
          :class="{'invalid-input': v$.site.englishDescription.$error }"
          maxlength="255"
        />
        <div
          v-for="error of v$.site.englishDescription.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="address"
          class="required-input"
        >{{ $t('entity.site.address.field') }}</label>
        <InputAddress
          id="address"
          v-model="v$.site.address.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.site.address.$error }"
          type="text"
        />
        <div
          v-for="error of v$.site.address.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label for="phoneNumber">{{ $t('entity.site.phone_number') }}</label>
        <InputPhoneNumber
          id="phoneNumber"
          v-model:phoneNumberCountryCode="v$.site.phoneNumberCountryCode.$model"
          v-model:phoneNumber="v$.site.phoneNumber.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.site.phoneNumber.$error }"
        />
        <div
          v-for="error of v$.site.phoneNumber.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="timezone"
          class="required-input"
        >{{ $t('entity.site.timezone') }}</label>
        <Dropdown
          id="timezone"
          v-model="v$.site.timezone.$model"
          class="col-12 mt-2 p-0"
          :options="timezones"
          :class="{'invalid-input': v$.site.timezone.$error }"
        />
        <div
          v-for="error of v$.site.timezone.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <div class="p-input mb-3">
        <label
          for="cluster"
          class="required-input"
        >{{ $t('entity.site.cluster') }}</label>
        <InputText
          id="cluster"
          v-model="v$.site.cluster.$model"
          class="col-12 mt-2"
          :class="{'invalid-input': v$.site.cluster.$error }"
          type="text"
          maxlength="255"
        />
        <div
          v-for="error of v$.site.cluster.$errors"
          :key="error.$uid"
          class="input-errors"
        >
          <div class="error-msg">
            {{ error.$message }}
          </div>
        </div>
      </div>

      <Message
        v-if="errors.length > 0"
        severity="error"
        :closable="false"
      >
        <ul class="list-none m-0 p-0">
          <li
            v-for="error of errors"
            :key="error.content"
          >
            {{ $t(error) }}
          </li>
        </ul>
      </Message>

      <div class="my-3 flex justify-content-between flex-wrap">
        <Button
          :label="$t('action.back')"
          icon="pi pi-arrow-left"
          class="p-button-secondary p-button-rounded p-button-outlined"
          @click="toSiteTable"
        />
        <Button
          type="submit"
          :label="isUpdate ? $t('action.crud.update') : $t('action.crud.create')"
          icon="pi pi-save"
          class="p-button-rounded p-button-outlined"
          :disabled="v$.$invalid"
        />
      </div>
    </form>
  </div>
</template>

<script>
import {useVuelidate} from "@vuelidate/core";
import {maxLength, phoneNumberPattern, required} from "@/i18n/i18nValidators";
import InputText from "primevue/inputtext";
import Button from "primevue/button";
import Message from "primevue/message";
import Toolbar from "primevue/toolbar";
import InputAddress from "@/components/InputAddress.vue";
import Textarea from "primevue/textarea";
import Dropdown from "primevue/dropdown";
import moment from "moment-timezone";
import InputPhoneNumber from "@/components/InputPhoneNumber.vue";

export default {
  name: 'SiteForm',
  components: {InputPhoneNumber, Textarea, InputAddress, Toolbar, InputText, Button, Message, Dropdown},
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      site: {
        code: '',
        name: '',
        frenchDescription: '',
        englishDescription: '',
        address: null,
        phoneNumberCountryCode: 'FR',
        phoneNumber: '',
        timezone: 'Europe/Paris',
        cluster: ''
      },
      timezones: [],
      errors: []
    }
  },
  computed: {
    isUpdate() {
      return this.$route.params.id;
    }
  },
  mounted() {
    this.timezones = moment.tz.names();
    if (this.isUpdate) this.fetchSite(this.$route.params.id);
  },
  validations() {
    return {
      site: {
        code: {required, maxLength: maxLength(10)},
        name: {required, maxLength: maxLength(255)},
        frenchDescription: {maxLength: maxLength(255)},
        englishDescription: {maxLength: maxLength(255)},
        address: {required},
        phoneNumberCountryCode: {},
        phoneNumber: {phoneNumberPattern: phoneNumberPattern(this.site.phoneNumberCountryCode)},
        timezone: {required, maxLength: maxLength(50)},
        cluster: {required, maxLength: maxLength(255)}
      }
    }
  },
  methods: {
    async fetchSite(id) {
      try {
        this.site = await this.$siteService.fetchById(id);
        if (!this.site) await this.$router.push("/site");
      } catch (error) {
        this.$emitter.emit('toast', {
          severity: 'error',
          message: error.response.data.map(error => this.$t(error)).join(', ')
        });
        await this.$router.push("/site");
      }
    },
    async submit() {
      const formErrors = await this.v$.$validate();
      if (!formErrors) return;

      this.errors = [];
      await this.createOrUpdate();
    },
    toSiteTable() {
      this.$router.push({name: 'site'});
    },
    async createOrUpdate() {
      try {
        if (this.isUpdate) {
          await this.$siteService.update(this.site);
          this.$emitter.emit('toast', {severity: 'success', message: 'sitepage.toast.updateSuccess'});
        } else {
          await this.$siteService.create(this.site);
          this.$emitter.emit('toast', {severity: 'success', message: 'sitepage.toast.createSuccess'});
        }
        this.toSiteTable();
      } catch (e) {
        this.errors = e.response.data;
      }
    }
  }
}
</script>
